/**
 * PrototypeChatRail: binds the Ask dock to an open Prototype.
 *
 * One durable conversation per prototype, bound the same way VisionChatRail's
 * ConversationPanel binds a parent-typed thread: on mount, upsert the
 * `parentType: "prototype"` conversation via POST /api/conversations (the
 * handler runs ON CONFLICT DO UPDATE, so repeat mounts resume the same row),
 * then hand the returned conversationId to the dock's thread. The prototype-parent
 * conversation is what activates the backend's prototype hydrator + drafting
 * prompt (the agent learns the prototypeId from the conversation's parent),
 * so revising continues in place — the conversation IS the editor. A
 * feed-session (`pulse_session`-parent) thread would never trigger that path.
 */
import { useEffect, useMemo, useState } from "react";
import type { Prototype } from "@yagni/shared";
import { useDockBinding } from "../shell/askDockStore.js";
import { captureUiError } from "../sentry.js";

export interface PrototypeChatRailProps {
  prototype: Prototype;
}

export function PrototypeChatRail({ prototype }: PrototypeChatRailProps) {
  const [conversationId, setConversationId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setConversationId(null);
    void (async () => {
      try {
        const res = await fetch("/api/conversations", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            parentType: "prototype",
            parentId: prototype.id,
            name: prototype.title,
          }),
        });
        if (cancelled) return;
        if (!res.ok) throw new Error(`Prototype conversation upsert failed: ${res.status}`);
        const conv = (await res.json()) as { id: string };
        if (!cancelled) setConversationId(conv.id);
      } catch (error) {
        // The rail keeps its empty state and the prototype still renders, but
        // a rail that never loads is reported rather than looking slow.
        if (cancelled) return;
        captureUiError(error instanceof Error ? error : new Error(String(error)), {
          area: "prototypes",
          operation: "chat_rail_conversation",
          severity: "warning",
          expected: false,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // The title only names the row on first creation; re-upserting on a
    // rename would refetch for no reason, so the effect keys on the id alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prototype.id]);

  const scopeContext = useMemo(
    () => ({
      prototypeId: prototype.id,
      ...(prototype.workItemId ? { workItemId: prototype.workItemId } : {}),
    }),
    [prototype.id, prototype.workItemId],
  );

  const emptyTitle = "Revise this prototype.";
  const emptyDetail =
    "Describe the change in plain words. Each ask becomes a new revision on the timeline.";

  useDockBinding({
    scope: {
      key: `prototype:${prototype.id}`,
      label: "this prototype",
      placeholder: "Ask for a revision…",
      opening: `${emptyTitle} ${emptyDetail}`,
      chips: ["Tighten the spacing", "Make it work on mobile"],
      scopeContext,
      conversationId,
    },
  });
  return null;
}
